// Istatistikler: ilan gelisimi, haftalik/aylik/yillik satis, goruntulenme, favori, harcamalar.
// Veri gunluk toplayicidan (Etsy'ye gitmez). Finans sayfasinin sekmeleri (kar-zarar, reklam,
// ilan sagligi) burada. Filtreler adres cubugunda tutulur (kaydedilip paylasilabilir).

import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { clsx } from "clsx";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import {
  BarChart3, LineChart as LineIcon, List, Wallet, Megaphone, HeartPulse, RefreshCw, Loader2, X, ExternalLink, Download, Info, ArrowUp, ArrowDown, Search,
} from "lucide-react";
import { authHeaders, getBackendUrl } from "../lib/http";
import { PnlTab, AdsTab, HealthTab } from "./FinancePage";

// ---------------- Veri tipleri (API: /api/stats) ----------------
type Kpi = {
  revenue: number; orders: number; units: number; productCost: number; fees: number; ads: number; other: number; ai: number;
  views: number; favorites: number; profit: number; aov: number; conversion: number | null; costUnknownOrders: number; feesFromLedger: boolean;
  orderProfit: number; aiUnassigned: number;
};
type SeriesRow = { key: string; revenue: number; units: number; orders: number; productCost: number; fees: number; ads: number; other: number; ai: number; views: number; favorites: number; profit: number };
type Overview = { kpi: Kpi; prev: Kpi | null; series: SeriesRow[]; costBreakdown: Array<{ category: string; amount: number }>; growth: Array<{ key: string; active: number; created: number }>; dataSince: string | null; from: string; to: string };
type Ads = { spend: number; revenue: number; clicks: number; impressions: number; conversions: number; periodDays: number; capturedAt: string };
type Row = {
  shopId: string; listingId: string; title: string; sku: string | null; imageUrl: string | null; price: number | null; quantity: number | null; state: string | null;
  createdAt: string | null; shippingProfileId: string | null; taxonomyId: number | null;
  views: number; favorites: number; units: number; orders: number; revenue: number; cost: number; fees: number; profit: number; costKnown: boolean;
  prevViews: number; prevUnits: number; prevRevenue: number; spark: number[]; viewSpark: number[];
  variationsSold: Record<string, number>; countries: Record<string, number>; ads: Ads | null;
  conversion: number | null; productType: string | null; vertical: string | null; custom: boolean; supplierSku: string | null; offeredVariations: string[]; tags: string[]; inPanel: boolean;
};
type Detail = {
  listing: { title: string; imageUrl: string | null; price: number | null; quantity: number | null; createdAt: string | null } | null;
  dataSince: string | null;
  daily: Array<{ key: string; views: number; favorites: number; units: number; revenue: number }>;
  variations: Array<{ name: string; units: number }>; countries: Array<{ country: string; units: number }>;
  orders: Array<{ receiptId: string; date: string; country: string | null; units: number; variations: string[]; revenue: number; fees: number; cost: number; netProfit: number | null; feesEstimated: boolean; shared: boolean }>;
  ads: Array<Ads & { id: string }>; aiSpend: { usd: number; calls: number } | null;
};
type Meta = { shops: Array<{ id: string; name: string; vertical: string | null }>; activeShopId: string | null; shippingProfiles: Record<string, string>; viewsSince: string | null; ordersSince: string | null };

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(`${getBackendUrl()}/api/stats${path}`, { ...init, headers: { ...authHeaders(), "Content-Type": "application/json", ...(init?.headers ?? {}) } });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `İstek başarısız (${r.status})`);
  return j.data as T;
}

// ---------------- Bicim ----------------
const usd = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: Math.abs(n) >= 1000 ? 0 : 2 });
const num = (n: number) => n.toLocaleString("tr-TR");
const pct = (n: number | null) => (n === null ? "–" : `%${n.toLocaleString("tr-TR", { maximumFractionDigits: 1 })}`);
const change = (cur: number, prev: number) => (prev === 0 ? (cur === 0 ? 0 : null) : ((cur - prev) / Math.abs(prev)) * 100);
const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n: number) => new Date(Date.now() - n * 86400_000).toISOString().slice(0, 10);

// Grafik paleti: dataviz referans paleti (acik tema), sabit sira — rengi varlik belirler, sira degil
const C = { blue: "#2a78d6", orange: "#eb6834", aqua: "#1baf7a", yellow: "#eda100", magenta: "#e87ba4", grid: "#e5e7eb", axis: "#6b7280" };

const CAT_LABELS: Record<string, string> = {
  refunds: "İadeler", fees_order: "İşlem ve ödeme ücretleri", fees_listing: "Listeleme ve yenileme", ads: "Etsy Ads", offsite_ads: "Offsite Ads",
  subscription: "Abonelik", shipping_labels: "Kargo etiketi", vat: "KDV (Etsy ücretleri)", credits: "Etsy kredileri", other_fees: "Diğer Etsy ücretleri", other_income: "Diğer gelir",
};

const PRESETS: Array<[string, string, () => [string, string]]> = [
  ["7", "Son 7 gün", () => [daysAgo(6), today()]],
  ["30", "Son 30 gün", () => [daysAgo(29), today()]],
  ["90", "Son 90 gün", () => [daysAgo(89), today()]],
  ["ytd", "Bu yıl", () => [`${new Date().getFullYear()}-01-01`, today()]],
  ["365", "Son 12 ay", () => [daysAgo(364), today()]],
];
const GROUPS: Array<[string, string]> = [["day", "Günlük"], ["week", "Haftalık"], ["month", "Aylık"], ["year", "Yıllık"]];
const TABS = [
  { id: "overview", label: "Genel bakış", icon: LineIcon },
  { id: "listings", label: "İlanlar", icon: List },
  { id: "pnl", label: "Kâr-zarar", icon: Wallet },
  { id: "ads", label: "Reklam", icon: Megaphone },
  { id: "health", label: "İlan sağlığı", icon: HeartPulse },
] as const;

const inputCls = "rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300";

export default function StatsPage() {
  const [sp, setSp] = useSearchParams();
  const set = (patch: Record<string, string | null>) => {
    const n = new URLSearchParams(sp);
    for (const [k, v] of Object.entries(patch)) if (v === null || v === "") n.delete(k); else n.set(k, v);
    setSp(n, { replace: true });
  };
  const tab = sp.get("tab") ?? "overview";
  const shop = sp.get("shop") ?? "all";
  const preset = sp.get("p") ?? "30";
  const [from, to] = preset === "custom" ? [sp.get("from") ?? daysAgo(29), sp.get("to") ?? today()] : (PRESETS.find((p) => p[0] === preset)?.[2] ?? PRESETS[1][2])();
  const group = sp.get("g") ?? (preset === "7" || preset === "30" ? "day" : preset === "90" ? "week" : "month");
  const compare = sp.get("cmp") !== "0";
  const qs = `shop=${shop}&from=${from}&to=${to}`;

  const meta = useQuery({ queryKey: ["stats-meta"], queryFn: () => api<Meta>("/meta"), staleTime: 10 * 60_000 });
  const qc = useQueryClient();
  const [collecting, setCollecting] = useState(false);
  const [collectMsg, setCollectMsg] = useState("");
  const [finKey, setFinKey] = useState(0);

  async function collectNow() {
    setCollecting(true); setCollectMsg("");
    try {
      await api("/collect", { method: "POST", body: JSON.stringify({ shop }) });
      await qc.invalidateQueries({ queryKey: ["stats"] });
      setCollectMsg("Veriler Etsy'den güncellendi.");
    } catch (e) { setCollectMsg(e instanceof Error ? e.message : "Güncellenemedi"); }
    finally { setCollecting(false); }
  }

  const finance = tab === "pnl" || tab === "ads" || tab === "health";
  return (
    <div className="mx-auto max-w-7xl p-6">
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-bold text-gray-900"><BarChart3 className="h-5 w-5 text-indigo-600" /> İstatistikler</h1>
          <p className="mt-1 text-sm text-gray-500">Satış, görüntülenme, favori, harcama ve kâr; ilan ilan, mağaza mağaza</p>
        </div>
        <div className="flex items-center gap-2">
          {collectMsg && <span className="text-xs text-gray-600">{collectMsg}</span>}
          {finance ? (
            <button onClick={() => setFinKey((k) => k + 1)} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
              <RefreshCw className="h-4 w-4" /> Etsy'den yenile
            </button>
          ) : (
            <button onClick={() => void collectNow()} disabled={collecting} title="Bugünün verisini şimdi Etsy'den çeker (normalde günde bir kez kendiliğinden)"
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              {collecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Şimdi güncelle
            </button>
          )}
        </div>
      </div>

      <div className="mb-4 flex gap-1 overflow-x-auto border-b border-gray-200">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => set({ tab: t.id === "overview" ? null : t.id })}
            className={clsx("-mb-px flex shrink-0 items-center gap-1.5 border-b-2 px-4 py-2 text-sm font-medium", tab === t.id ? "border-indigo-600 text-indigo-700" : "border-transparent text-gray-500 hover:text-gray-800")}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {finance ? (
        <>
          <p className="mb-4 flex items-center gap-1.5 text-xs text-gray-500"><Info className="h-3.5 w-3.5" /> Bu sekme üst menüde seçili mağazayı gösterir ve Etsy'den canlı okunur.</p>
          {tab === "pnl" && <PnlTab refreshKey={finKey} />}
          {tab === "ads" && <AdsTab refreshKey={finKey} />}
          {tab === "health" && <HealthTab refreshKey={finKey} />}
        </>
      ) : (
        <>
          {/* Filtre cubugu */}
          <div className="mb-5 flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
            <label className="sr-only" htmlFor="st-shop">Mağaza</label>
            <select id="st-shop" value={shop} onChange={(e) => set({ shop: e.target.value === "all" ? null : e.target.value })} className={inputCls}>
              <option value="all">Tüm mağazalar</option>
              {meta.data?.shops.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <label className="sr-only" htmlFor="st-period">Dönem</label>
            <select id="st-period" value={preset} onChange={(e) => set({ p: e.target.value === "30" ? null : e.target.value, g: null })} className={inputCls}>
              {PRESETS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
              <option value="custom">Özel aralık</option>
            </select>
            {preset === "custom" && (
              <>
                <input type="date" aria-label="Başlangıç" value={from} max={to} onChange={(e) => set({ from: e.target.value })} className={inputCls} />
                <span className="text-sm text-gray-500">–</span>
                <input type="date" aria-label="Bitiş" value={to} min={from} max={today()} onChange={(e) => set({ to: e.target.value })} className={inputCls} />
              </>
            )}
            {tab === "overview" && (
              <>
                <label className="sr-only" htmlFor="st-group">Gruplama</label>
                <select id="st-group" value={group} onChange={(e) => set({ g: e.target.value })} className={inputCls}>
                  {GROUPS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                </select>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={compare} onChange={(e) => set({ cmp: e.target.checked ? null : "0" })} className="h-4 w-4 rounded border-gray-300 text-indigo-600" />
                  Önceki dönemle karşılaştır
                </label>
              </>
            )}
            {meta.data?.viewsSince && (
              <span className="ml-auto text-xs text-gray-500" title="Etsy görüntülenme geçmişi vermiyor; günlük kayıt bu tarihte başladı">
                Görüntülenme/favori kaydı: {meta.data.viewsSince}’den beri
              </span>
            )}
          </div>
          {tab === "overview" && <OverviewTab qs={qs} group={group} compare={compare} />}
          {tab === "listings" && <ListingsTab qs={qs} shop={shop} from={from} to={to} meta={meta.data} sp={sp} set={set} />}
        </>
      )}
    </div>
  );
}

// ---------------- Ortak parcalar ----------------
function Loading() {
  return <p className="flex items-center gap-2 py-10 text-sm text-gray-500"><Loader2 className="h-5 w-5 animate-spin text-indigo-600" /> Yükleniyor…</p>;
}
function ErrorBox({ error }: { error: unknown }) {
  return <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error instanceof Error ? error.message : "Veri alınamadı"}</p>;
}
function Delta({ cur, prev, invert }: { cur: number; prev: number | undefined; invert?: boolean }) {
  if (prev === undefined) return null;
  const c = change(cur, prev);
  if (c === null) return <span className="text-xs text-gray-500">yeni</span>;
  const good = invert ? c <= 0 : c >= 0;
  return (
    <span className={clsx("inline-flex items-center gap-0.5 text-xs font-medium", Math.abs(c) < 0.5 ? "text-gray-500" : good ? "text-emerald-700" : "text-red-600")}>
      {c > 0 ? <ArrowUp className="h-3 w-3" /> : c < 0 ? <ArrowDown className="h-3 w-3" /> : null}{pct(Math.abs(Math.round(c * 10) / 10))}
    </span>
  );
}
function Tile({ label, value, cur, prev, invert, sub }: { label: string; value: string; cur: number; prev?: number; invert?: boolean; sub?: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className="mt-1 text-xl font-semibold tabular-nums text-gray-900">{value}</p>
      <div className="mt-1 flex items-center gap-2"><Delta cur={cur} prev={prev} invert={invert} />{sub && <span className="text-xs text-gray-500">{sub}</span>}</div>
    </div>
  );
}
function ChartCard({ title, children, note }: { title: string; children: React.ReactNode; note?: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <h2 className="mb-3 text-sm font-semibold text-gray-700">{title}</h2>
      <div className="h-56">{children}</div>
      {note && <p className="mt-2 text-xs text-gray-500">{note}</p>}
    </div>
  );
}
const axisProps = { stroke: C.axis, fontSize: 11, tickLine: false, axisLine: false } as const;
const tipStyle = { contentStyle: { borderRadius: 8, borderColor: C.grid, fontSize: 12 }, labelStyle: { color: "#111827", fontWeight: 600 } };
const shortKey = (k: string) => (k.length === 10 ? k.slice(5) : k);

/** Kucuk trend cizgisi (tablo satiri) */
function Spark({ data, color }: { data: number[]; color: string }) {
  const max = Math.max(1, ...data);
  if (!data.some((v) => v > 0)) return <span className="text-xs text-gray-300">—</span>;
  const pts = data.map((v, i) => `${(i / Math.max(1, data.length - 1)) * 60},${18 - (v / max) * 16}`).join(" ");
  return <svg width="60" height="20" aria-hidden="true"><polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" /></svg>;
}

// ---------------- Genel bakis ----------------
function OverviewTab({ qs, group, compare }: { qs: string; group: string; compare: boolean }) {
  const q = useQuery({ queryKey: ["stats", "overview", qs, group, compare], queryFn: () => api<Overview>(`/overview?${qs}&group=${group}${compare ? "&compare=1" : ""}`) });
  if (q.isLoading) return <Loading />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const d = q.data!;
  const k = d.kpi, p = d.prev ?? undefined;
  const series = d.series.map((s) => ({ ...s, label: shortKey(s.key) }));
  const growth = d.growth.map((g) => ({ ...g, label: shortKey(g.key) }));
  const totalCost = k.productCost + k.fees + k.ads + k.other + k.ai;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        <Tile label="Ciro" value={usd(k.revenue)} cur={k.revenue} prev={p?.revenue} sub="vergisiz, kargo dahil" />
        <Tile label="Sipariş kârı" value={usd(k.orderProfit)} cur={k.orderProfit} prev={p?.orderProfit} sub="Etsy kesintisi + ürün maliyeti sonrası" />
        <Tile label="Net kâr" value={usd(k.profit)} cur={k.profit} prev={p?.profit} sub="reklam ve tüm mağaza giderleri sonrası" />
        <Tile label="Sipariş" value={num(k.orders)} cur={k.orders} prev={p?.orders} sub={`${num(k.units)} adet`} />
        <Tile label="Görüntülenme" value={num(k.views)} cur={k.views} prev={p?.views} sub={`dönüşüm ${pct(k.conversion)}`} />
        <Tile label="Favori" value={num(k.favorites)} cur={k.favorites} prev={p?.favorites} />
        <Tile label="Ürün maliyeti" value={usd(k.productCost)} cur={k.productCost} prev={p?.productCost} invert sub="tedarikçi + kargo" />
        <Tile label="Etsy ücretleri" value={usd(k.fees)} cur={k.fees} prev={p?.fees} invert />
        <Tile label="Reklam" value={usd(k.ads)} cur={k.ads} prev={p?.ads} invert sub={k.ads > 0 ? `ciroya oran ${pct(Math.round((k.ads / Math.max(1, k.revenue)) * 1000) / 10)}` : undefined} />
        <Tile label="Yapay zekâ" value={usd(k.ai)} cur={k.ai} prev={p?.ai} invert sub={k.aiUnassigned > 0 ? `${usd(k.aiUnassigned)}'ı mağazası bilinmeyen eski kayıt` : "görsel/analiz üretimi"} />
        <Tile label={k.other <= 0 ? "Kredi / iade geliri" : "Diğer gider"} value={usd(Math.abs(k.other))} cur={-k.other} prev={p ? -p.other : undefined} sub={`ort. sipariş ${usd(k.aov)}`} />
        <Tile label="Toplam gider" value={usd(totalCost)} cur={totalCost} prev={p ? p.productCost + p.fees + p.ads + p.other + p.ai : undefined} invert />
      </div>
      <p className="text-xs text-gray-500">
        Net kâr = ciro − ürün maliyeti − Etsy ücretleri (işlem, ödeme, liste, ücret KDV'si, abonelik) − reklam (Etsy Ads + Offsite) − yapay zekâ + kredi/iade.
        Sipariş kârı yalnız siparişe düşen kesintileri sayar; reklam ve liste ücreti gibi mağaza giderleri onun dışında kalır.
      </p>
      {(k.costUnknownOrders > 0 || !k.feesFromLedger) && (
        <p className="flex items-center gap-1.5 rounded-lg bg-amber-50 p-3 text-sm text-amber-700">
          <Info className="h-4 w-4 shrink-0" />
          {k.costUnknownOrders > 0 && `${k.costUnknownOrders} siparişin ürün maliyeti bilinmiyor (kâr eksik hesaplanır). `}
          {!k.feesFromLedger && "Bu dönemde Etsy hesap hareketi kaydı yok; ücretler sıfır görünür."}
        </p>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCard title="Ciro ve net kâr">
          <ResponsiveContainer>
            <LineChart data={series} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid stroke={C.grid} vertical={false} />
              <XAxis dataKey="label" {...axisProps} minTickGap={16} />
              <YAxis {...axisProps} width={56} tickFormatter={(v) => `$${num(v)}`} />
              <Tooltip {...tipStyle} formatter={(v: number) => usd(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="revenue" name="Ciro" stroke={C.blue} strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="profit" name="Net kâr" stroke={C.aqua} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Giderler">
          <ResponsiveContainer>
            <BarChart data={series} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid stroke={C.grid} vertical={false} />
              <XAxis dataKey="label" {...axisProps} minTickGap={16} />
              <YAxis {...axisProps} width={56} tickFormatter={(v) => `$${num(v)}`} />
              <Tooltip {...tipStyle} formatter={(v: number) => usd(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="productCost" name="Ürün maliyeti" stackId="c" fill={C.blue} />
              <Bar dataKey="fees" name="Etsy ücretleri" stackId="c" fill={C.orange} />
              <Bar dataKey="ads" name="Reklam" stackId="c" fill={C.aqua} />
              <Bar dataKey="ai" name="Yapay zekâ" stackId="c" fill={C.yellow} />
              <Bar dataKey="other" name="Diğer" stackId="c" fill={C.magenta} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Satış adedi">
          <ResponsiveContainer>
            <BarChart data={series} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid stroke={C.grid} vertical={false} />
              <XAxis dataKey="label" {...axisProps} minTickGap={16} />
              <YAxis {...axisProps} width={40} allowDecimals={false} />
              <Tooltip {...tipStyle} />
              <Bar dataKey="units" name="Adet" fill={C.blue} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Görüntülenme" note={d.dataSince ? `Etsy geçmiş vermiyor; günlük kayıt ${d.dataSince} tarihinde başladı.` : "Henüz günlük kayıt yok."}>
          <ResponsiveContainer>
            <LineChart data={series} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid stroke={C.grid} vertical={false} />
              <XAxis dataKey="label" {...axisProps} minTickGap={16} />
              <YAxis {...axisProps} width={48} allowDecimals={false} />
              <Tooltip {...tipStyle} />
              <Line type="monotone" dataKey="views" name="Görüntülenme" stroke={C.blue} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Favori">
          <ResponsiveContainer>
            <LineChart data={series} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid stroke={C.grid} vertical={false} />
              <XAxis dataKey="label" {...axisProps} minTickGap={16} />
              <YAxis {...axisProps} width={40} allowDecimals={false} />
              <Tooltip {...tipStyle} />
              <Line type="monotone" dataKey="favorites" name="Favori" stroke={C.orange} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Aktif ilan sayısı" note="Günlük kayıttaki aktif ilanlar (kayıt başladıktan sonra).">
          <ResponsiveContainer>
            <LineChart data={growth} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid stroke={C.grid} vertical={false} />
              <XAxis dataKey="label" {...axisProps} minTickGap={16} />
              <YAxis {...axisProps} width={44} allowDecimals={false} domain={["auto", "auto"]} />
              <Tooltip {...tipStyle} />
              <Line type="stepAfter" dataKey="active" name="Aktif ilan" stroke={C.blue} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Yeni açılan ilanlar" note="Etsy'deki ilk açılış tarihine göre (şu an aktif ilanlar).">
          <ResponsiveContainer>
            <BarChart data={growth} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid stroke={C.grid} vertical={false} />
              <XAxis dataKey="label" {...axisProps} minTickGap={16} />
              <YAxis {...axisProps} width={40} allowDecimals={false} />
              <Tooltip {...tipStyle} />
              <Bar dataKey="created" name="Yeni ilan" fill={C.aqua} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <h2 className="mb-3 text-sm font-semibold text-gray-700">Etsy hesap hareketleri (dönem)</h2>
          {d.costBreakdown.length === 0 ? <p className="text-sm text-gray-500">Kayıt yok.</p> : (
            <table className="w-full text-sm">
              <tbody>
                {d.costBreakdown.map((c) => (
                  <tr key={c.category} className="border-t border-gray-100">
                    <td className="py-1.5 text-gray-700">{CAT_LABELS[c.category] ?? c.category}</td>
                    <td className={clsx("py-1.5 text-right tabular-nums", c.amount < 0 ? "text-red-600" : "text-emerald-700")}>{usd(c.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------- Ilanlar ----------------
type SortKey = "views" | "favorites" | "units" | "revenue" | "profit" | "conversion" | "growth" | "createdAt" | "adsSpend" | "price";
const SALES_FILTERS: Array<[string, string]> = [["", "Satış: hepsi"], ["sold", "Satışı olan"], ["nosale", "Satışı olmayan"], ["noviews", "Hiç görüntülenmeyen"], ["interest", "İlgi var, satış yok"]];

function ListingsTab({ qs, shop, from, to, meta, sp, set }: {
  qs: string; shop: string; from: string; to: string; meta: Meta | undefined; sp: URLSearchParams; set: (p: Record<string, string | null>) => void;
}) {
  const q = useQuery({ queryKey: ["stats", "listings", qs], queryFn: () => api<{ rows: Row[]; dataSince: string | null }>(`/listings?${qs}`) });
  const [detail, setDetail] = useState<Row | null>(null);
  const f = {
    q: sp.get("q") ?? "", type: sp.get("type") ?? "", ship: sp.get("ship") ?? "", sales: sp.get("sales") ?? "", state: sp.get("state") ?? "active",
    ads: sp.get("ads") ?? "", custom: sp.get("custom") ?? "", varf: sp.get("var") ?? "", pmin: sp.get("pmin") ?? "", pmax: sp.get("pmax") ?? "",
    cfrom: sp.get("cfrom") ?? "", cto: sp.get("cto") ?? "", sort: (sp.get("sort") as SortKey) ?? "revenue", dir: sp.get("dir") ?? "desc",
  };
  const page = Number(sp.get("page") ?? "1") || 1;
  const rows = q.data?.rows ?? [];
  const shopName = useMemo(() => new Map(meta?.shops.map((s) => [s.id, s.name]) ?? []), [meta]);
  const types = useMemo(() => [...new Set(rows.map((r) => r.productType).filter(Boolean) as string[])].sort(), [rows]);
  const ships = useMemo(() => [...new Set(rows.map((r) => r.shippingProfileId).filter(Boolean) as string[])], [rows]);
  const vars = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rows) for (const v of new Set([...r.offeredVariations, ...Object.keys(r.variationsSold)])) m.set(v, (m.get(v) ?? 0) + 1);
    return [...m.entries()].filter(([, n]) => n > 1).map(([v]) => v).sort((a, b) => a.localeCompare(b, "tr"));
  }, [rows]);

  const growth = (r: Row) => change(r.revenue || r.views, r.revenue ? r.prevRevenue : r.prevViews);
  const filtered = useMemo(() => {
    const needle = f.q.toLocaleLowerCase("tr");
    const out = rows.filter((r) => {
      if (f.state === "active" && r.state && r.state !== "active") return false;
      if (f.state === "inactive" && (!r.state || r.state === "active")) return false;
      if (needle && ![r.title, r.sku ?? "", r.listingId, r.supplierSku ?? "", ...r.tags].some((x) => x.toLocaleLowerCase("tr").includes(needle))) return false;
      if (f.type && r.productType !== f.type) return false;
      if (f.ship && r.shippingProfileId !== f.ship) return false;
      if (f.sales === "sold" && r.units === 0) return false;
      if (f.sales === "nosale" && r.units > 0) return false;
      if (f.sales === "noviews" && r.views > 0) return false;
      if (f.sales === "interest" && !(r.units === 0 && (r.favorites > 0 || r.views >= 20))) return false;
      if (f.ads === "yes" && !(r.ads && r.ads.spend > 0)) return false;
      if (f.ads === "no" && r.ads && r.ads.spend > 0) return false;
      if (f.custom === "yes" && !r.custom) return false;
      if (f.custom === "no" && r.custom) return false;
      if (f.varf && !r.offeredVariations.includes(f.varf) && !r.variationsSold[f.varf]) return false;
      if (f.pmin && (r.price ?? 0) < Number(f.pmin)) return false;
      if (f.pmax && (r.price ?? 0) > Number(f.pmax)) return false;
      if (f.cfrom && (!r.createdAt || r.createdAt < f.cfrom)) return false;
      if (f.cto && (!r.createdAt || r.createdAt > f.cto)) return false;
      return true;
    });
    const val = (r: Row): number => {
      switch (f.sort) {
        case "conversion": return r.conversion ?? -1;
        case "growth": return growth(r) ?? -1e9;
        case "createdAt": return r.createdAt ? Date.parse(r.createdAt) : 0;
        case "adsSpend": return r.ads?.spend ?? 0;
        case "price": return r.price ?? 0;
        default: return r[f.sort];
      }
    };
    const s = f.dir === "asc" ? 1 : -1;
    return out.sort((a, b) => (val(a) - val(b)) * s);
  }, [rows, sp]); // eslint-disable-line react-hooks/exhaustive-deps

  const PAGE = 50;
  const pageRows = filtered.slice((page - 1) * PAGE, page * PAGE);
  const tot = filtered.reduce((a, r) => ({ views: a.views + r.views, favorites: a.favorites + r.favorites, units: a.units + r.units, revenue: a.revenue + r.revenue, profit: a.profit + r.profit }), { views: 0, favorites: 0, units: 0, revenue: 0, profit: 0 });
  const sortBy = (k: SortKey) => set({ sort: k, dir: f.sort === k && f.dir === "desc" ? "asc" : "desc", page: null });
  const fset = (k: string, v: string) => set({ [k]: v || null, page: null });
  const activeFilters = ["q", "type", "ship", "sales", "ads", "custom", "var", "pmin", "pmax", "cfrom", "cto"].filter((k) => sp.get(k)).length + (f.state !== "active" ? 1 : 0);

  function exportCsv() {
    const head = ["Mağaza", "Etsy no", "Başlık", "SKU", "Fiyat", "Görüntülenme", "Favori", "Satış adedi", "Sipariş", "Ciro", "Maliyet", "Ücretler", "Kâr", "Dönüşüm %", "Açılış"];
    const lines = filtered.map((r) => [shopName.get(r.shopId) ?? "", r.listingId, r.title, r.sku ?? "", r.price ?? "", r.views, r.favorites, r.units, r.orders, r.revenue, r.cost, r.fees, r.profit, r.conversion ?? "", r.createdAt ?? ""]);
    const csv = [head, ...lines].map((l) => l.map((x) => `"${String(x).replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" }));
    a.download = `ilan-istatistik-${from}_${to}.csv`;
    a.click();
  }

  const Th = ({ k, children, right = true }: { k?: SortKey; children: React.ReactNode; right?: boolean }) => (
    <th className={clsx("px-3 py-2 font-medium", right && "text-right")} aria-sort={k && f.sort === k ? (f.dir === "asc" ? "ascending" : "descending") : undefined}>
      {k ? (
        <button onClick={() => sortBy(k)} className="inline-flex items-center gap-0.5 hover:text-gray-900">
          {children}{f.sort === k && (f.dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
        </button>
      ) : children}
    </th>
  );
  const sel = (id: string, label: string, value: string, key: string, opts: Array<[string, string]>) => (
    <><label className="sr-only" htmlFor={id}>{label}</label>
      <select id={id} value={value} onChange={(e) => fset(key, e.target.value)} className={clsx(inputCls, "max-w-[12rem]")}>
        {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select></>
  );

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
            <input aria-label="Ara" value={f.q} onChange={(e) => fset("q", e.target.value)} placeholder="Başlık, etiket, SKU, Etsy no…" className={clsx(inputCls, "w-64 pl-8")} />
          </div>
          {sel("lf-state", "Durum", f.state, "state", [["active", "Aktif ilanlar"], ["inactive", "Aktif olmayan (satışı geçmiş)"], ["all", "Tümü"]])}
          {sel("lf-sales", "Satış", f.sales, "sales", SALES_FILTERS)}
          {sel("lf-type", "Ürün türü", f.type, "type", [["", "Tüm ürün türleri"], ...types.map((t) => [t, t] as [string, string])])}
          {sel("lf-ship", "Kargo profili", f.ship, "ship", [["", "Tüm kargo profilleri"], ...ships.map((s) => [s, meta?.shippingProfiles[s] ?? `Profil ${s}`] as [string, string])])}
          {sel("lf-ads", "Reklam", f.ads, "ads", [["", "Reklam: hepsi"], ["yes", "Reklamda (harcama var)"], ["no", "Reklamda değil"]])}
          {sel("lf-custom", "Kişiselleştirme", f.custom, "custom", [["", "Kişiselleştirme: hepsi"], ["yes", "Kişiselleştirmeli"], ["no", "Kişiselleştirmesiz"]])}
          {vars.length > 0 && sel("lf-var", "Varyasyon", f.varf, "var", [["", "Tüm varyasyonlar"], ...vars.map((v) => [v, v] as [string, string])])}
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-gray-700">
          <span className="text-xs text-gray-500">Fiyat</span>
          <input aria-label="En düşük fiyat" type="number" min={0} value={f.pmin} onChange={(e) => fset("pmin", e.target.value)} placeholder="en az $" className={clsx(inputCls, "w-24")} />
          <input aria-label="En yüksek fiyat" type="number" min={0} value={f.pmax} onChange={(e) => fset("pmax", e.target.value)} placeholder="en çok $" className={clsx(inputCls, "w-24")} />
          <span className="ml-2 text-xs text-gray-500">Açılış</span>
          <input aria-label="Açılış başlangıcı" type="date" value={f.cfrom} onChange={(e) => fset("cfrom", e.target.value)} className={inputCls} />
          <input aria-label="Açılış bitişi" type="date" value={f.cto} onChange={(e) => fset("cto", e.target.value)} className={inputCls} />
          {activeFilters > 0 && (
            <button onClick={() => set({ q: null, type: null, ship: null, sales: null, ads: null, custom: null, var: null, pmin: null, pmax: null, cfrom: null, cto: null, state: null, page: null })}
              className="rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100">Filtreleri temizle ({activeFilters})</button>
          )}
          <button onClick={exportCsv} disabled={!filtered.length} className="ml-auto inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            <Download className="h-4 w-4" /> CSV indir
          </button>
        </div>
      </div>

      {q.isLoading ? <Loading /> : q.isError ? <ErrorBox error={q.error} /> : (
        <>
          <p className="text-sm text-gray-600">
            <b>{num(filtered.length)}</b> ilan · {num(tot.views)} görüntülenme · {num(tot.favorites)} favori · {num(tot.units)} satış · {usd(tot.revenue)} ciro · {usd(tot.profit)} kâr
          </p>
          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs text-gray-500">
                <tr>
                  <th className="px-3 py-2 font-medium">İlan</th>
                  <Th k="views">Görüntülenme</Th>
                  <Th k="favorites">Favori</Th>
                  <Th k="units">Satış</Th>
                  <Th k="revenue">Ciro</Th>
                  <Th k="profit">Kâr</Th>
                  <Th k="conversion">Dönüşüm</Th>
                  <Th k="growth">Önceki döneme göre</Th>
                  <Th k="adsSpend">Reklam</Th>
                  <Th k="price">Fiyat</Th>
                  <Th k="createdAt">Açılış</Th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((r) => {
                  const g = growth(r);
                  return (
                    <tr key={`${r.shopId}|${r.listingId}`} className="cursor-pointer border-t border-gray-100 hover:bg-gray-50" onClick={() => setDetail(r)}>
                      <td className="px-3 py-2">
                        <button className="flex max-w-md items-center gap-3 text-left" onClick={(e) => { e.stopPropagation(); setDetail(r); }}>
                          {r.imageUrl ? <img src={r.imageUrl} alt="" loading="lazy" className="h-10 w-10 shrink-0 rounded-lg object-cover" /> : <span className="h-10 w-10 shrink-0 rounded-lg bg-gray-100" />}
                          <span className="min-w-0">
                            <span className="line-clamp-1 text-gray-900">{r.title || `İlan ${r.listingId}`}</span>
                            <span className="block text-xs text-gray-500">
                              {shop === "all" && `${shopName.get(r.shopId) ?? ""} · `}{r.sku ?? r.supplierSku ?? r.listingId}{r.productType ? ` · ${r.productType}` : ""}{r.state && r.state !== "active" ? " · aktif değil" : ""}
                            </span>
                          </span>
                        </button>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums"><div className="flex items-center justify-end gap-2"><Spark data={r.viewSpark} color={C.blue} />{num(r.views)}</div></td>
                      <td className="px-3 py-2 text-right tabular-nums">{num(r.favorites)}</td>
                      <td className="px-3 py-2 text-right tabular-nums"><div className="flex items-center justify-end gap-2"><Spark data={r.spark} color={C.aqua} />{num(r.units)}</div></td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.revenue ? usd(r.revenue) : "–"}</td>
                      <td className={clsx("px-3 py-2 text-right tabular-nums", r.profit < 0 ? "text-red-600" : "text-gray-900")} title={r.costKnown ? undefined : "Bazı kalemlerin maliyeti bilinmiyor"}>
                        {r.units ? `${usd(r.profit)}${r.costKnown ? "" : "*"}` : "–"}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{pct(r.conversion)}</td>
                      <td className="px-3 py-2 text-right"><Delta cur={r.revenue || r.views} prev={r.revenue ? r.prevRevenue : r.prevViews} />{g === null && <span className="sr-only">yeni</span>}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-xs text-gray-600" title={r.ads ? `Son ${r.ads.periodDays} gün · ${r.ads.clicks} tık · ${r.ads.impressions} gösterim · okuma ${r.ads.capturedAt.slice(0, 10)}` : "Eklentinin Reklam sekmesi açılınca kaydedilir"}>
                        {r.ads && r.ads.spend > 0 ? <>{usd(r.ads.spend)}<br />ROAS {r.ads.spend ? (r.ads.revenue / r.ads.spend).toFixed(2) : "–"}</> : "–"}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.price !== null ? usd(r.price) : "–"}</td>
                      <td className="px-3 py-2 text-right text-xs text-gray-500">{r.createdAt ?? "–"}</td>
                    </tr>
                  );
                })}
                {!pageRows.length && <tr><td colSpan={11} className="px-3 py-10 text-center text-sm text-gray-500">Filtrelere uyan ilan yok.</td></tr>}
              </tbody>
            </table>
          </div>
          {filtered.length > PAGE && (
            <div className="flex items-center justify-end gap-2 text-sm text-gray-600">
              <button disabled={page <= 1} onClick={() => set({ page: String(page - 1) })} className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 disabled:opacity-50">Önceki</button>
              <span>{page} / {Math.ceil(filtered.length / PAGE)}</span>
              <button disabled={page * PAGE >= filtered.length} onClick={() => set({ page: String(page + 1) })} className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 disabled:opacity-50">Sonraki</button>
            </div>
          )}
          <p className="text-xs text-gray-500">
            Kâr: sipariş kâr kaydı varsa gerçek Etsy ücretleri, yoksa tahmini ücret (%9.5 + $0.45) ile; * bazı kalemlerin ürün maliyeti bilinmiyor.
            Reklam: liste bazında Etsy API'de yok; eklentinin Reklam sekmesi her açıldığında son dönem kaydedilir.
          </p>
        </>
      )}
      {detail && <ListingDetail row={detail} from={from} to={to} shopName={shopName.get(detail.shopId) ?? ""} onClose={() => setDetail(null)} />}
    </div>
  );
}

// ---------------- Ilan ayrintisi ----------------
function ListingDetail({ row, from, to, shopName, onClose }: { row: Row; from: string; to: string; shopName: string; onClose: () => void }) {
  const q = useQuery({ queryKey: ["stats", "detail", row.shopId, row.listingId, from, to], queryFn: () => api<Detail>(`/listing/${row.shopId}/${row.listingId}?from=${from}&to=${to}`) });
  const d = q.data;
  const daily = (d?.daily ?? []).map((x) => ({ ...x, label: shortKey(x.key) }));
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={onClose} onKeyDown={(e) => e.key === "Escape" && onClose()} role="presentation">
      <div role="dialog" aria-modal="true" aria-label="İlan ayrıntısı" className="flex h-full w-full max-w-3xl flex-col bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
          <div className="flex min-w-0 items-center gap-3">
            {row.imageUrl && <img src={row.imageUrl} alt="" className="h-14 w-14 shrink-0 rounded-lg object-cover" />}
            <div className="min-w-0">
              <h2 className="line-clamp-2 text-base font-semibold text-gray-900">{row.title}</h2>
              <p className="text-xs text-gray-500">{shopName} · {row.sku ?? row.listingId}{row.price !== null ? ` · ${usd(row.price)}` : ""}{row.createdAt ? ` · açılış ${row.createdAt}` : ""}</p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <a href={`https://www.etsy.com/listing/${row.listingId}`} target="_blank" rel="noreferrer" aria-label="Etsy'de aç" title="Etsy'de aç" className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100"><ExternalLink className="h-4 w-4" /></a>
            <button onClick={onClose} aria-label="Kapat" title="Kapat" className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100"><X className="h-4 w-4" /></button>
          </div>
        </div>
        <div className="space-y-4 overflow-y-auto p-5">
          <div className="grid grid-cols-3 gap-2 md:grid-cols-6">
            <Tile label="Görüntülenme" value={num(row.views)} cur={row.views} prev={row.prevViews} />
            <Tile label="Favori" value={num(row.favorites)} cur={row.favorites} />
            <Tile label="Satış" value={num(row.units)} cur={row.units} prev={row.prevUnits} />
            <Tile label="Ciro" value={usd(row.revenue)} cur={row.revenue} prev={row.prevRevenue} />
            <Tile label="Kâr" value={usd(row.profit)} cur={row.profit} sub={row.costKnown ? undefined : "maliyet eksik"} />
            <Tile label="Dönüşüm" value={pct(row.conversion)} cur={row.conversion ?? 0} />
          </div>
          {q.isLoading ? <Loading /> : q.isError ? <ErrorBox error={q.error} /> : d && (
            <>
              <div className="grid gap-4 md:grid-cols-2">
                <ChartCard title="Günlük görüntülenme" note={d.dataSince ? `Kayıt ${d.dataSince}’den beri` : undefined}>
                  <ResponsiveContainer>
                    <LineChart data={daily}><CartesianGrid stroke={C.grid} vertical={false} /><XAxis dataKey="label" {...axisProps} minTickGap={16} /><YAxis {...axisProps} width={36} allowDecimals={false} /><Tooltip {...tipStyle} />
                      <Line type="monotone" dataKey="views" name="Görüntülenme" stroke={C.blue} strokeWidth={2} dot={false} /></LineChart>
                  </ResponsiveContainer>
                </ChartCard>
                <ChartCard title="Günlük satış">
                  <ResponsiveContainer>
                    <BarChart data={daily}><CartesianGrid stroke={C.grid} vertical={false} /><XAxis dataKey="label" {...axisProps} minTickGap={16} /><YAxis {...axisProps} width={30} allowDecimals={false} /><Tooltip {...tipStyle} />
                      <Bar dataKey="units" name="Adet" fill={C.aqua} radius={[4, 4, 0, 0]} /></BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-xl border border-gray-200 p-4">
                  <h3 className="mb-2 text-sm font-semibold text-gray-700">Hangi varyasyon satıyor</h3>
                  {d.variations.length === 0 ? <p className="text-sm text-gray-500">Bu dönemde satış yok.</p> : d.variations.map((v) => (
                    <div key={v.name} className="mb-1.5">
                      <div className="flex justify-between text-xs text-gray-700"><span>{v.name}</span><span className="tabular-nums">{v.units}</span></div>
                      <div className="h-1.5 rounded-full bg-gray-100"><div className="h-1.5 rounded-full" style={{ width: `${(v.units / d.variations[0].units) * 100}%`, background: C.blue }} /></div>
                    </div>
                  ))}
                </div>
                <div className="rounded-xl border border-gray-200 p-4">
                  <h3 className="mb-2 text-sm font-semibold text-gray-700">Ülkeler</h3>
                  {d.countries.length === 0 ? <p className="text-sm text-gray-500">Bu dönemde satış yok.</p> : (
                    <ul className="space-y-1 text-sm">{d.countries.map((c) => <li key={c.country} className="flex justify-between text-gray-700"><span>{c.country}</span><span className="tabular-nums">{c.units}</span></li>)}</ul>
                  )}
                  {d.aiSpend && <p className="mt-3 border-t border-gray-100 pt-2 text-xs text-gray-500">Ürünün yapay zekâ harcaması (tüm zamanlar): <b className="text-gray-700">{usd(d.aiSpend.usd)}</b> · {d.aiSpend.calls} çağrı</p>}
                </div>
              </div>
              {d.ads.length > 0 && (
                <div className="rounded-xl border border-gray-200 p-4">
                  <h3 className="mb-2 text-sm font-semibold text-gray-700">Etsy Ads (eklentinin okumaları)</h3>
                  <table className="w-full text-sm">
                    <thead className="text-left text-xs text-gray-500"><tr><th className="py-1">Okuma</th><th className="py-1 text-right">Dönem</th><th className="py-1 text-right">Harcama</th><th className="py-1 text-right">Reklam satışı</th><th className="py-1 text-right">ROAS</th><th className="py-1 text-right">Tık</th><th className="py-1 text-right">Gösterim</th></tr></thead>
                    <tbody>{d.ads.map((a) => (
                      <tr key={a.id} className="border-t border-gray-100"><td className="py-1">{a.capturedAt.slice(0, 10)}</td><td className="py-1 text-right">{a.periodDays} gün</td><td className="py-1 text-right tabular-nums">{usd(a.spend)}</td><td className="py-1 text-right tabular-nums">{usd(a.revenue)}</td><td className="py-1 text-right tabular-nums">{a.spend ? (a.revenue / a.spend).toFixed(2) : "–"}</td><td className="py-1 text-right tabular-nums">{num(a.clicks)}</td><td className="py-1 text-right tabular-nums">{num(a.impressions)}</td></tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
              <div className="rounded-xl border border-gray-200">
                <h3 className="px-4 pt-3 text-sm font-semibold text-gray-700">Siparişler ({d.orders.length})</h3>
                {d.orders.length === 0 ? <p className="p-4 text-sm text-gray-500">Bu dönemde sipariş yok.</p> : (
                  <table className="mt-2 w-full text-sm">
                    <thead className="bg-gray-50 text-left text-xs text-gray-500"><tr><th className="px-3 py-2">Tarih</th><th className="px-3 py-2">Varyasyon</th><th className="px-3 py-2">Ülke</th><th className="px-3 py-2 text-right">Ciro</th><th className="px-3 py-2 text-right">Ücret</th><th className="px-3 py-2 text-right">Maliyet</th><th className="px-3 py-2 text-right">Net kâr</th></tr></thead>
                    <tbody>{d.orders.map((o) => (
                      <tr key={o.receiptId} className="border-t border-gray-100">
                        <td className="px-3 py-2 text-xs text-gray-600">{o.date}<br /><span className="text-gray-500">#{o.receiptId}</span></td>
                        <td className="px-3 py-2 text-xs text-gray-700">{o.variations.join(" / ") || "–"}{o.units > 1 ? ` · ${o.units} adet` : ""}{o.shared && <span className="block text-gray-500">siparişte başka ürün de var</span>}</td>
                        <td className="px-3 py-2 text-xs">{o.country ?? "–"}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{usd(o.revenue)}</td>
                        <td className="px-3 py-2 text-right tabular-nums" title={o.feesEstimated ? "Tahmini" : "Etsy hesap hareketlerinden"}>{usd(o.fees)}{o.feesEstimated ? "*" : ""}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{usd(o.cost)}</td>
                        <td className={clsx("px-3 py-2 text-right tabular-nums", (o.netProfit ?? 0) < 0 && "text-red-600")}>{o.netProfit === null ? "?" : usd(o.netProfit)}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
